import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { hasChildrenColumns, isActionColumn } from 'src/utils'

import { ResizableHeaderCell } from './columnHeader'
import type { ColumnWidthMap } from './hooks'
import { readRowKey, TreadmillBody } from './TreadmillBody'
import type { TreadmillBodyInfo } from './TreadmillBody'
import {
  applyColumnState,
  buildColumnLayout,
  columnStateKey,
  headerPinOffsetsOf,
  HORIZONTAL_WINDOW_MIN_COLUMNS
} from './treadmillMath'
import type {
  ColumnStateMap,
  HeaderPinOffsets,
  TreadmillLeaf
} from './treadmillMath'
import { createSelectionStore } from './treadmillSelection'

const VIRTUAL_COLUMNS_THRESHOLD = 7
const VIRTUAL_ROWS_THRESHOLD = 50
const VIRTUAL_COLUMN_WIDTH = 180
const VIRTUAL_CONTROL_COLUMN_WIDTH = 48
const NO_STRETCH = 0
const WIDTH_SEPARATOR = ','
const FIXED_HEADER_BRANCH_SCROLL_Y = 'auto'

type VirtualizationOptions<T> = {
  columns: ColumnsType<T>
  columnCount: number
  containerWidth?: number
  columnsMap?: ColumnStateMap
  columnWidths?: ColumnWidthMap
  dataSource: readonly T[]
  pageSize?: number
  virtual?: boolean
  scroll?: TableProps<T>['scroll']
  tableLayout?: TableProps<T>['tableLayout']
  components?: TableProps<T>['components']
  sticky?: TableProps<T>['sticky']
  rowKey?: TableProps<T>['rowKey']
  rowSelection?: TableProps<T>['rowSelection']
  expandable?: TableProps<T>['expandable']
  rowClassName?: TableProps<T>['rowClassName']
  onRow?: TableProps<T>['onRow']
  emptyText?: ReactNode
}

type Virtualization<T> = {
  virtual: boolean
  columnKeys: string[]
  columns: ColumnsType<T>
  scroll: TableProps<T>['scroll']
  tableLayout: TableProps<T>['tableLayout']
  components: TableProps<T>['components']
  sticky: TableProps<T>['sticky']
  rowSelection?: TableProps<T>['rowSelection']
  expandable?: TableProps<T>['expandable']
  headerPins?: HeaderPinOffsets
}

const readColumnWidth = <T,>(column: ColumnsType<T>[number]): number => {
  const width = typeof column.width === 'number' ? column.width : 0

  return Math.max(width, column.minWidth ?? 0)
}

const readControlColumnWidth = (columnWidth?: number | string): number =>
  typeof columnWidth === 'number' ? columnWidth : VIRTUAL_CONTROL_COLUMN_WIDTH

const collectLeafBaseWidths = <T,>(
  columns: ColumnsType<T>,
  bases: number[],
  isFixedWidth: boolean[],
  columnWidths: ColumnWidthMap | undefined
) => {
  columns.forEach((column) => {
    if (hasChildrenColumns(column)) {
      collectLeafBaseWidths(column.children, bases, isFixedWidth, columnWidths)

      return
    }

    bases.push(readColumnWidth(column) || VIRTUAL_COLUMN_WIDTH)
    isFixedWidth.push(
      Boolean(column.fixed) || columnWidths?.[String(column.key)] !== undefined
    )
  })
}

const stretchToContainer = (
  bases: number[],
  isFixedWidth: boolean[],
  isHidden: boolean[],
  availableWidth?: number
): number[] => {
  if (!availableWidth) {
    return bases
  }

  const total = bases.reduce(
    (sum, width, index) => (isHidden[index] ? sum : sum + width),
    0
  )
  const stretchableTotal = bases.reduce(
    (sum, width, index) =>
      isFixedWidth[index] || isHidden[index] ? sum : sum + width,
    0
  )

  if (total >= availableWidth || !stretchableTotal) {
    return bases
  }

  const factor = 1 + (availableWidth - total) / stretchableTotal

  return bases.map((width, index) =>
    isFixedWidth[index] || isHidden[index] ? width : Math.floor(width * factor)
  )
}

const collectLeafVisibility = <T,>(
  columns: ColumnsType<T>,
  columnsMap: ColumnStateMap | undefined,
  hidden: boolean[],
  isParentHidden = false
) => {
  columns.forEach((column, index) => {
    const isHidden =
      isParentHidden ||
      columnsMap?.[columnStateKey(column, index)]?.show === false

    if (hasChildrenColumns(column)) {
      collectLeafVisibility(column.children, columnsMap, hidden, isHidden)

      return
    }

    hidden.push(isHidden)
  })
}

const collectLeaves = <T,>(columns: ColumnsType<T>): Array<TreadmillLeaf<T>> =>
  columns.flatMap((column) =>
    hasChildrenColumns(column)
      ? collectLeaves(column.children)
      : [column as TreadmillLeaf<T>]
  )

const sizeColumns = <T,>(
  columns: ColumnsType<T>,
  finalWidths: number[],
  cursor: { index: number }
): {
  columns: ColumnsType<T>
  leaves: Array<TreadmillLeaf<T>>
  width: number
} => {
  let width = 0
  const leaves: Array<TreadmillLeaf<T>> = []

  const sized = columns.map((column) => {
    if (hasChildrenColumns(column)) {
      const group = sizeColumns(column.children, finalWidths, cursor)

      width += group.width
      leaves.push(...group.leaves)

      return { ...column, children: group.columns }
    }

    const columnWidth = finalWidths[cursor.index] ?? VIRTUAL_COLUMN_WIDTH
    const leaf = { ...column, width: columnWidth } as TreadmillLeaf<T>

    cursor.index += 1
    width += columnWidth
    leaves.push(leaf)

    return leaf
  })

  return { columns: sized, leaves, width }
}

const applyResizedWidths = <T,>(
  columns: ColumnsType<T>,
  columnWidths: ColumnWidthMap | undefined
): ColumnsType<T> =>
  columns.map((column, index) => {
    if (hasChildrenColumns(column)) {
      return {
        ...column,
        children: applyResizedWidths(column.children, columnWidths)
      }
    }

    const key = columnStateKey(column, index)

    if (isActionColumn(column)) {
      return { ...column, key } as ColumnsType<T>[number]
    }

    const resized = columnWidths?.[key]

    return {
      ...column,
      key,
      ...(resized ? { width: resized, minWidth: undefined } : {}),
      onHeaderCell: (headerColumn: unknown, headerIndex: number) => ({
        ...column.onHeaderCell?.(
          headerColumn as Parameters<
            NonNullable<typeof column.onHeaderCell>
          >[0],
          headerIndex
        ),
        'data-column-key': key
      })
    } as ColumnsType<T>[number]
  })

export const useTableVirtualization = <T,>({
  columns,
  columnCount,
  containerWidth,
  columnsMap,
  columnWidths,
  dataSource,
  pageSize,
  virtual,
  scroll,
  tableLayout,
  components,
  sticky,
  rowKey,
  rowSelection,
  expandable,
  rowClassName,
  onRow,
  emptyText
}: VirtualizationOptions<T>): Virtualization<T> => {
  const resizableColumns = useMemo(
    () => applyResizedWidths(columns, columnWidths),
    [columns, columnWidths]
  )

  const headerComponents = useMemo(
    () => ({
      ...components,
      header: { ...components?.header, cell: ResizableHeaderCell }
    }),
    [components]
  )

  const isSupported =
    !components?.body && !expandable?.expandedRowRender && !scroll?.y

  const rowBudget = pageSize ?? dataSource.length

  const enabled =
    isSupported && virtual !== false && rowBudget > VIRTUAL_ROWS_THRESHOLD

  const isWide =
    isSupported &&
    (virtual !== false || columnCount > VIRTUAL_COLUMNS_THRESHOLD)

  const selectionWidth = rowSelection
    ? readControlColumnWidth(rowSelection.columnWidth)
    : 0

  const bases = useMemo(() => {
    if (!isWide) {
      return null
    }

    const widths: number[] = []
    const isFixedWidth: boolean[] = []

    collectLeafBaseWidths(resizableColumns, widths, isFixedWidth, columnWidths)

    return { widths, isFixedWidth }
  }, [isWide, resizableColumns, columnWidths])

  const availableWidth = containerWidth ? containerWidth - selectionWidth : 0

  const stretchWidth = bases ? availableWidth : NO_STRETCH

  const leafHidden: boolean[] = []

  collectLeafVisibility(resizableColumns, columnsMap, leafHidden)

  const finalWidthsSignature = bases
    ? stretchToContainer(
        bases.widths,
        bases.isFixedWidth,
        leafHidden,
        stretchWidth
      ).join(WIDTH_SEPARATOR)
    : ''

  const sized = useMemo(
    () =>
      finalWidthsSignature
        ? sizeColumns(
            resizableColumns,
            finalWidthsSignature.split(WIDTH_SEPARATOR).map(Number),
            { index: 0 }
          )
        : null,
    [finalWidthsSignature, resizableColumns]
  )

  const visible = useMemo(() => {
    const shown = applyColumnState(
      sized?.columns ?? resizableColumns,
      columnsMap
    )
    const leaves = collectLeaves(shown)

    return {
      columns: shown,
      keys: shown.map((column) => String(column.key)),
      leaves,
      width: leaves.reduce((total, leaf) => total + (leaf.width ?? 0), 0)
    }
  }, [sized, resizableColumns, columnsMap])

  const totalWidth = visible.width + selectionWidth

  const headerPins = useMemo(
    () => headerPinOffsetsOf(buildColumnLayout(visible.leaves, selectionWidth)),
    [visible.leaves, selectionWidth]
  )

  const [selectionStore] = useState(createSelectionStore)
  const liveRef = useRef({
    rowSelection,
    onRow,
    rowClassName,
    rowKey,
    dataSource
  })

  useEffect(() => {
    liveRef.current = { rowSelection, onRow, rowClassName, rowKey, dataSource }

    selectionStore.setHandlers({
      isDisabled: (record) =>
        liveRef.current.rowSelection?.getCheckboxProps?.(record as T)?.disabled,
      toggle: (rowKeyValue, checked) => {
        const live = liveRef.current
        const onSelectionChange = live.rowSelection?.onChange

        if (!onSelectionChange) {
          return
        }

        const current = new Set(live.rowSelection?.selectedRowKeys ?? [])

        if (checked) {
          current.add(rowKeyValue)
        } else {
          current.delete(rowKeyValue)
        }

        const nextKeys = [...current]
        const nextRows = live.dataSource.filter((row, index) =>
          current.has(readRowKey(row, index, live.rowKey))
        )

        onSelectionChange(nextKeys, nextRows as T[], { type: 'single' })
      }
    })
  })

  const selectedRowKeys = rowSelection?.selectedRowKeys

  useEffect(() => {
    selectionStore.setKeys(new Set(selectedRowKeys ?? []))
  }, [selectionStore, selectedRowKeys])

  const stableOnRow = useCallback(
    (record: T, index?: number) => liveRef.current.onRow?.(record, index) ?? {},
    []
  )

  const stableRowClassName = useCallback((record: T, index: number) => {
    const source = liveRef.current.rowClassName

    return typeof source === 'function'
      ? source(record, index, 0)
      : (source as string | undefined)
  }, [])

  const hasSelection = Boolean(rowSelection)
  const hasOnRow = Boolean(onRow)
  const hasRowClassName = Boolean(rowClassName)
  const leaves = visible.leaves

  const renderTreadmillBody = useCallback(
    (bodyData: readonly T[], info: TreadmillBodyInfo) => (
      <TreadmillBody<T>
        data={bodyData}
        leaves={leaves ?? []}
        totalWidth={totalWidth}
        rowKey={rowKey}
        selectionWidth={selectionWidth}
        selectionStore={hasSelection ? selectionStore : null}
        rowClassName={hasRowClassName ? stableRowClassName : undefined}
        onRow={hasOnRow ? stableOnRow : undefined}
        emptyText={emptyText}
        info={info}
      />
    ),
    [
      leaves,
      totalWidth,
      rowKey,
      selectionWidth,
      hasSelection,
      hasOnRow,
      hasRowClassName,
      selectionStore,
      stableOnRow,
      stableRowClassName,
      emptyText
    ]
  )

  const virtualComponents = useMemo(
    () => ({ ...headerComponents, body: renderTreadmillBody }),
    [headerComponents, renderTreadmillBody]
  )

  if (!sized) {
    return {
      virtual: false,
      columnKeys: visible.keys,
      columns: visible.columns,
      scroll: scroll ?? { x: 'max-content' },
      tableLayout,
      components: headerComponents as TableProps<T>['components'],
      sticky,
      rowSelection,
      expandable
    }
  }

  const isColumnHeavy =
    isSupported &&
    virtual !== false &&
    visible.leaves.length >= HORIZONTAL_WINDOW_MIN_COLUMNS

  if (!enabled && !isColumnHeavy) {
    return {
      virtual: false,
      columnKeys: visible.keys,
      columns: sized.columns,
      scroll: {
        ...scroll,
        x: typeof scroll?.x === 'number' ? scroll.x : totalWidth
      },
      tableLayout: 'fixed',
      components: headerComponents as TableProps<T>['components'],
      sticky,
      rowSelection: rowSelection && {
        ...rowSelection,
        columnWidth: selectionWidth
      },
      expandable
    }
  }

  return {
    virtual: true,
    columnKeys: visible.keys,
    columns: sized.columns,
    scroll: {
      ...scroll,
      x: typeof scroll?.x === 'number' ? scroll.x : totalWidth,
      y: FIXED_HEADER_BRANCH_SCROLL_Y
    },
    tableLayout: 'fixed',
    components: virtualComponents as TableProps<T>['components'],
    sticky: false,
    rowSelection: rowSelection && {
      ...rowSelection,
      columnWidth: selectionWidth
    },
    expandable,
    headerPins
  }
}
